import { useAuth } from "@dl/web-core-js/auth";
import {
  Badge,
  Button,
  Card,
  Dialog,
  Field,
  Input,
  Select,
  TabNotPorted,
  cn,
  useConfirm,
} from "@dl/web-core-js/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { ExternalLink, Pencil, Plus, Unlink } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ProductLink } from "../../components/ProductLink";
import { ColourSchemeClient } from "../../api/configurator";
import {
  imageUrl,
  LazyProductClient,
  ProductClient,
  type LazyProductDTO,
  type ProductDTO,
  type VariationType,
} from "../../api/product";

/**
 * The products sharing this product's variation group — a colour or size
 * family — with the group's management: link products in, unlink them, and set
 * each sibling's colour/size axes.
 *
 * Group membership persists immediately, one PUT per product, exactly as the
 * Vaadin grid did: half-linked state is worse than a moment of latency.
 */
export function VariationsTab({ draft }: { draft: ProductDTO }) {
  const { t } = useTranslation();
  const { http, canWrite } = useAuth();
  const confirm = useConfirm();
  const queryClient = useQueryClient();

  const client = useMemo(() => new ProductClient(http), [http]);
  const lazy = useMemo(() => new LazyProductClient(http), [http]);

  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<ProductDTO | null>(null);

  const group = draft.variation_group ?? null;

  const { data, isLoading } = useQuery({
    queryKey: ["product", "variation-group", group],
    queryFn: () => client.byVariationGroup(group as number),
    enabled: group !== null,
  });

  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: ["product", "variation-group", group] });

  const unlink = async (sibling: ProductDTO) => {
    const confirmed = await confirm({
      title: t("product.variations.unlink.title", "Unlink this product from the group?"),
      message: t(
        "product.variations.unlink.message",
        "{{sku}} keeps existing. It just stops being a variation of this family.",
        { sku: sibling.metadata?.sku ?? "" },
      ),
      variant: "danger",
      confirmLabel: t("product.variations.unlink", "Unlink"),
    });
    if (!confirmed) return;

    try {
      await client.updateProduct({ ...sibling, variation_group: null });
      await refresh();
      toast.success(t("notification.saved", "Saved"));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t("error.generic", "Failed"));
    }
  };

  const linkProducts = async (selected: LazyProductDTO[]) => {
    setAdding(false);
    try {
      // One PUT per product, matching the backend's shape — there is no batch
      // group-assign endpoint.
      for (const candidate of selected) {
        const full = await client.findById(candidate.id);
        if (full) await client.updateProduct({ ...full, variation_group: group });
      }
      await refresh();
      toast.success(t("product.variations.linked", "{{count}} product(s) linked", { count: selected.length }));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t("error.generic", "Failed"));
    }
  };

  if (group === null) {
    return (
      <TabNotPorted
        message={t("product.variations.no-group", "This product is not part of a variation group.")}
      />
    );
  }

  if (isLoading) {
    return (
      <div className="flex h-32 items-center justify-center">
        <span className="size-5 animate-spin rounded-full border-2 border-accent border-t-transparent" />
      </div>
    );
  }

  const siblings = (data ?? [])
    .slice()
    .sort((a, b) => (a.metadata?.sku ?? "").localeCompare(b.metadata?.sku ?? ""));

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-3">
        <p className="text-[12px] text-ink-faint">
          {t("product.variations.group", "Variation group")} {group} ·{" "}
          {t("product.variations.count", "{{count}} products", { count: siblings.length })}
        </p>
        {canWrite && (
          <Button
            size="sm"
            variant="primary"
            icon={<Plus className="size-4" />}
            onClick={() => setAdding(true)}
            className="ml-auto"
          >
            {t("product.variations.add", "Link products")}
          </Button>
        )}
      </div>

      <ul className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
        {siblings.map((sibling) => {
          const current = sibling.id === draft.id;
          const thumbnail = imageUrl(sibling.images?.[0]);

          const body = (
            <Card
              className={cn(
                "flex items-center gap-3 p-3",
                current ? "border-accent bg-accent-soft" : "hover:border-accent",
              )}
            >
              {thumbnail ? (
                <img
                  src={thumbnail}
                  alt=""
                  loading="lazy"
                  className="size-10 shrink-0 rounded border border-line object-cover"
                />
              ) : (
                <span className="size-10 shrink-0 rounded border border-line bg-surface-sunk" />
              )}

              <div className="min-w-0 flex-1">
                <p className="truncate text-[13px] font-medium text-ink">
                  {sibling.metadata?.sku ?? ""}
                </p>
                <p className="truncate text-[12px] text-ink-muted">
                  {sibling.description_en?.product_name ?? ""}
                </p>
                <p className="mt-1 flex flex-wrap gap-1">
                  {(sibling.variations ?? []).map((variation) => (
                    <Badge key={`${variation.type}-${variation.id}`} variant="neutral" size="sm">
                      {variation.type}: {variation.value}
                    </Badge>
                  ))}
                </p>
              </div>

              {current ? (
                <Badge variant="accent" size="sm">
                  {t("product.variations.this", "This one")}
                </Badge>
              ) : (
                <ExternalLink className="size-4 shrink-0 text-ink-faint" />
              )}
            </Card>
          );

          return (
            <li key={sibling.id} className="group/card relative">
              {current ? (
                body
              ) : (
                <Link
                  to="/products/$productId"
                  params={{ productId: String(sibling.id) }}
                  className="block"
                >
                  {body}
                </Link>
              )}

              {canWrite && (
                <span className="absolute right-2 top-2 hidden gap-1 group-hover/card:flex">
                  <Button
                    size="sm"
                    variant="secondary"
                    className="size-7 p-0"
                    aria-label={t("product.variations.edit", "Edit colour and size")}
                    onClick={(event) => {
                      event.preventDefault();
                      setEditing(sibling);
                    }}
                    icon={<Pencil className="size-3.5" />}
                  />
                  {!current && (
                    <Button
                      size="sm"
                      variant="secondary"
                      className="size-7 p-0"
                      aria-label={t("product.variations.unlink", "Unlink")}
                      onClick={(event) => {
                        event.preventDefault();
                        void unlink(sibling);
                      }}
                      icon={<Unlink className="size-3.5" />}
                    />
                  )}
                </span>
              )}
            </li>
          );
        })}
      </ul>

      {adding && (
        <VariationPickerDialog
          onClose={() => setAdding(false)}
          onConfirm={(selected) => void linkProducts(selected)}
          type={draft.type}
          excludeIds={siblings.map((sibling) => sibling.id)}
          lazy={lazy}
        />
      )}

      {editing && (
        <AxisEditDialog
          product={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void refresh();
          }}
          client={client}
        />
      )}
    </div>
  );
}

/** Colour and size for one sibling — the two axes the grid let you edit inline. */
function AxisEditDialog({
  product,
  onClose,
  onSaved,
  client,
}: {
  product: ProductDTO;
  onClose: () => void;
  onSaved: () => void;
  client: ProductClient;
}) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const axis = (type: VariationType) =>
    product.variations?.find((variation) => variation.type === type)?.value ?? "";
  // The colour is one of the product colours (0077), set up on Engineering › Colours.
  const schemes = useQuery({
    queryKey: ["manufacturing", "colour", "scheme"],
    queryFn: () => new ColourSchemeClient(http).findAll(),
    staleTime: 5 * 60_000,
  });
  const colourOptions = (schemes.data ?? []).map((scheme) => ({ value: scheme.sales_name, label: scheme.sales_name }));

  const [color, setColor] = useState(axis("COLOR"));
  const [size, setSize] = useState(axis("SIZE"));
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      // Rebuild the two managed axes, keeping each existing row's id so it is updated in place;
      // an emptied value removes its entry. The size is saved as typed ("Full/Double"); the colour
      // is a product colour, matched by the server to its own (0077).
      const others = (product.variations ?? []).filter(
        (variation) => variation.type !== "COLOR" && variation.type !== "SIZE",
      );
      const idOf = (type: VariationType) =>
        product.variations?.find((variation) => variation.type === type)?.id ?? 0;
      const next = [...others];
      if (color.trim()) next.push({ id: idOf("COLOR"), type: "COLOR" as const, value: color.trim() });
      if (size.trim()) next.push({ id: idOf("SIZE"), type: "SIZE" as const, value: size.trim() });

      await client.updateProduct({ ...product, variations: next });
      toast.success(t("notification.saved", "Saved"));
      onSaved();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t("error.generic", "Failed"));
      setSaving(false);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={t("product.variations.edit", "Edit colour and size")}
      description={product.metadata?.sku ?? ""}
      size="sm"
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            {t("button.cancel", "Cancel")}
          </Button>
          <Button variant="primary" loading={saving} onClick={() => void save()}>
            {t("button.save", "Save")}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field
          label={t("product.variations.color", "Colour")}
          htmlFor="axis-color"
          hint={t("product.variations.color.hint", "One of the product colours. A new one is added on Engineering, Colours, Product colours.")}
        >
          <Select
            id="axis-color"
            autoFocus
            options={colourOptions}
            placeholder={t("product.variations.color.none", "No colour")}
            value={color}
            onChange={(event) => setColor(event.target.value)}
          />
        </Field>
        <Field label={t("product.variations.size", "Size")} htmlFor="axis-size">
          <Input id="axis-size" value={size} onChange={(event) => setSize(event.target.value)} />
        </Field>
      </div>
    </Dialog>
  );
}

/** Candidates: same product type only, minus what's already in the group. */
function VariationPickerDialog({
  onClose,
  onConfirm,
  type,
  excludeIds,
  lazy,
}: {
  onClose: () => void;
  onConfirm: (selected: LazyProductDTO[]) => void;
  type: ProductDTO["type"];
  excludeIds: number[];
  lazy: LazyProductClient;
}) {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<Set<number>>(new Set());

  const { data, isLoading } = useQuery({
    queryKey: ["product", "variation-candidates", type],
    queryFn: () => lazy.byType(type),
    staleTime: 60_000,
  });

  const needle = query.trim().toLowerCase();
  const candidates = (data ?? [])
    .filter((product) => !excludeIds.includes(product.id))
    .filter(
      (product) =>
        needle === "" ||
        `${product.metadata?.sku ?? ""} ${product.description_en?.product_name ?? ""} ${(product.variations ?? [])
          .map((variation) => variation.value)
          .join(" ")}`
          .toLowerCase()
          .includes(needle),
    )
    .slice(0, 200);

  const toggle = (id: number) =>
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <Dialog
      open
      onClose={onClose}
      title={t("product.variations.add", "Link products")}
      description={t("product.variations.add.description", "Only products of the same type can share a group.")}
      size="lg"
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("button.cancel", "Cancel")}
          </Button>
          <Button
            variant="primary"
            disabled={selected.size === 0}
            onClick={() => onConfirm((data ?? []).filter((product) => selected.has(product.id)))}
          >
            {t("button.add", "Add")} ({selected.size})
          </Button>
        </>
      }
    >
      <Input
        value={query}
        autoFocus
        placeholder={t("grid.search", "Search")}
        onChange={(event) => setQuery(event.target.value)}
      />

      {isLoading ? (
        <div className="flex h-32 items-center justify-center">
          <span className="size-5 animate-spin rounded-full border-2 border-accent border-t-transparent" />
        </div>
      ) : (
        <ul className="mt-2 flex flex-col">
          {candidates.map((product) => (
            <li key={product.id}>
              <label className="flex cursor-pointer items-center gap-2.5 rounded px-2 py-1.5 hover:bg-surface-sunk">
                <input
                  type="checkbox"
                  checked={selected.has(product.id)}
                  onChange={() => toggle(product.id)}
                  className="size-3.5 accent-[var(--accent)]"
                />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[13px] font-medium text-ink">
                    <ProductLink id={product.id}>{product.metadata?.sku ?? ""}</ProductLink>
                  </span>
                  <span className="block truncate text-[12px] text-ink-muted">
                    {product.description_en?.product_name ?? ""}
                  </span>
                </span>
                {/* The colour and size are what tells two SKUs of a family apart; without them
                    the list is a wall of near-identical names. */}
                <span className="flex shrink-0 flex-wrap justify-end gap-1">
                  {(product.variations ?? []).length === 0 ? (
                    <span className="text-[11px] text-ink-faint">{t("product.variations.none", "no variation data")}</span>
                  ) : (
                    (product.variations ?? []).map((variation) => (
                      <Badge key={`${variation.type}-${variation.id ?? variation.value}`} variant="neutral" size="sm">
                        {variation.type}: {variation.value}
                      </Badge>
                    ))
                  )}
                </span>
              </label>
            </li>
          ))}
        </ul>
      )}
    </Dialog>
  );
}
